import {Component, inject, signal, Signal, viewChild, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {DxPaginationModule, DxTextBoxModule} from 'devextreme-angular';
import {ValueChangedEvent} from 'devextreme/ui/text_box';
import {ToastService} from '../../../../core/services/technique/toast.service';
import {GenericDatagridService} from '../../../../core/services/generics/generic-datagrid.service';
import {
  PlcProduitComplementaireService,
  PRODUIT_COMPLEMENTAIRE_PAGE_SIZE
} from '../../../../core/services/entities/plc-produit-complementaire.service';
import {PlcProduitComplementaireDTO} from '../../../../core/dtos/plc-produit-complementaire-dto';
import {
  PeriodeConditionnementDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {JourToggle} from '../../../../core/models/gestion-produits/declinaison/complementaire/plc/jour-toggle';
import {
  ProduitComplementaireParametrageService
} from '../../../../core/services/entities/produit-complementaire-parametrage.service';
import {PointDeLivraisonDTO} from '../../../../core/dtos/point-de-livraison-d-t-o';
import {MSG_KEY, MSG_SEVERITY} from '../../../../core/constants';
import {ProduitComplementaireSaveResultDTO} from '../../../../core/dtos/produit/declinaison/complementaire/produit-complementaire-save-result-dto';
import {
  ProduitComplementaireSuppressionBloqueeDTO
} from '../../../../core/dtos/produit/declinaison/complementaire/produit-complementaire-suppression-bloquee-dto';
import {ResponseWrapper} from '../../../../core/suppliers/wrappers/response-wrapper';
import {
  DialogSuppressionBloqueeComponent
} from '../../../../shared/produits-complementaires/dialog-suppression-bloquee/dialog-suppression-bloquee.component';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {SharedModule} from '../../../../shared/shared.module';
import {
  DialogAjoutProduitComplementaireComponent
} from './dialog-ajout-produit-complementaire/dialog-ajout-produit-complementaire.component';
import {
  DialogPeriodeConditionnementComponent
} from '../../../../shared/produits-complementaires/dialog-periode-conditionnement/dialog-periode-conditionnement.component';
import {ProduitComplementaireGridComponent} from '../../../../shared/produits-complementaires/produit-complementaire-grid/produit-complementaire-grid.component';

@Component({
  selector: 'yo-point-de-livraison-produits-complementaires',
  templateUrl: './point-de-livraison-produits-complementaires.component.html',
  styleUrls: ['./point-de-livraison-produits-complementaires.component.scss'],
  standalone: true,
  imports: [DxPaginationModule, DxTextBoxModule, SharedModule, ProduitComplementaireGridComponent,
            DialogAjoutProduitComplementaireComponent, DialogPeriodeConditionnementComponent, DialogSuppressionBloqueeComponent],
  encapsulation: ViewEncapsulation.None
})
export class PointDeLivraisonProduitsComplementairesComponent {

  readonly PAGE_SIZE = PRODUIT_COMPLEMENTAIRE_PAGE_SIZE;
  readonly ButtonThemeEnum = ButtonThemeEnum;

  private readonly gds = inject(GenericDatagridService);
  private readonly plcProduitComplementaireSvc = inject(PlcProduitComplementaireService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastSvc = inject(ToastService);
  private readonly paramSvc = inject(ProduitComplementaireParametrageService);

  private readonly grid = viewChild(ProduitComplementaireGridComponent);

  plc: PointDeLivraisonDTO;

  readonly suppressionBloqueeList = signal<ProduitComplementaireSuppressionBloqueeDTO[]>([]);

  readonly dialogAjoutVisible = signal<boolean>(false);

  readonly dialogPeriodeVisible = signal<boolean>(false);

  readonly produitPeriode = signal<PlcProduitComplementaireDTO>(null);

  readonly produitComplementaireList: Signal<PlcProduitComplementaireDTO[]> = this.plcProduitComplementaireSvc.currentPageList;

  readonly totalElements = this.plcProduitComplementaireSvc.gridTotalSignal;

  readonly currentPage = this.plcProduitComplementaireSvc.gridPageSignal;

  readonly hasChanges = this.plcProduitComplementaireSvc.hasChangesSignal;

  readonly plcSubscription = this.route.parent.data
      .pipe(takeUntilDestroyed())
      .subscribe(data => {
        this.plc = data.plcSupplier.pointDeLivraison;
        this.plcProduitComplementaireSvc.initPlc(this.plc?.id ?? null);
        this.grid()?.clearFiltreColonne();
      });

  canEdit = (): boolean => this.gds.canModify(this.plc?.client);

  openDialogAjout = (): void => this.dialogAjoutVisible.set(true);

  openDialogPeriode = (produitComplementaire: PlcProduitComplementaireDTO): void => {
    this.produitPeriode.set(produitComplementaire);
    this.dialogPeriodeVisible.set(true);
  };

  onPeriodeListChange = (periodeList: PeriodeConditionnementDTO[]): void =>
    this.plcProduitComplementaireSvc.applyPeriodeConditionnementList(this.produitPeriode()?.produitDeclinaisonId, periodeList);

  onPageIndexChange = (page: number): void => this.plcProduitComplementaireSvc.goToPage(page);

  onSearchChanged = (event: ValueChangedEvent): void =>
    this.plcProduitComplementaireSvc.searchGrid(event.value ?? '');

  onLigneEditee = (): void => this.plcProduitComplementaireSvc.collectPageEdits();

  toggleJour = (jour: JourToggle): void =>
    this.plcProduitComplementaireSvc.toggleJourConsommation(jour.produitDeclinaisonId, jour.jourId);

  supprimer = (produitComplementaire: PlcProduitComplementaireDTO): void =>
    this.plcProduitComplementaireSvc.markDeleted(produitComplementaire);

  save = (): void => {
    this.grid()?.saveEditData().then(() => {
      const changes = this.plcProduitComplementaireSvc.buildChanges();
      if (!changes.hasChanges()) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.INFO, `Aucune modification à enregistrer`);
        return;
      }

      const errorMessage = this.paramSvc.buildParametrageIncompletMessage(changes.produitComplementaireToEditList);
      if (errorMessage) {
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.ERROR, errorMessage);
        return;
      }

      this.plcProduitComplementaireSvc.saveChanges(changes)
          .subscribe((response: ResponseWrapper<ProduitComplementaireSaveResultDTO>) => {
            const suppressionBloqueeList = response?.one?.suppressionBloqueeList ?? [];
            if (suppressionBloqueeList.length) {
              this.suppressionBloqueeList.set(suppressionBloqueeList);
              return;
            }
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Produits complémentaires enregistrés avec succès`);
          });
    });
  };
}
